import { Skeleton } from '@/components/ui-kit/skeleton';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui-kit/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { Textarea } from '@/components/ui-kit/textarea';
import { LEAVE_RULES } from '@/constant/hr/leave-rule-catalog';
import { useHasPermission } from '@/hooks/use-has-permission';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm, Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  useGetMyLeaveTypes,
  useLeaveRequestPreview,
  useSubmitLeaveRequest,
} from '../../hooks/use-leave';
import {
  leaveRequestFormSchema,
  LeaveRequestFormValues,
} from '../../schemas/leave-request-form-schema';
import { SickLeaveDocumentField } from '../sick-leave-document-field/sick-leave-document-field';

/** Hours as "7.5", without trailing zeros. */
const hoursText = (hours: number) => String(Math.round(hours * 100) / 100);

/** The permission of the split TOIL rule: only its holders may charge a default leave to a TOIL pool. */
const SPLIT_TOIL_PERMISSION =
  LEAVE_RULES.find(
    (rule) =>
      rule.group === 'toilBalance' &&
      'splitByCategory' in rule.params &&
      rule.params.splitByCategory
  )?.permission ?? '';

/** The working days from which the sick leave certificate rule asks for a doctor's document. */
const SICK_DOCUMENT_MIN_DAYS =
  (
    LEAVE_RULES.find((rule) => rule.group === 'sickLeaveCertificate')?.params as
      { minDays: number } | undefined
  )?.minDays ?? null;

const EMPTY_FORM: LeaveRequestFormValues = {
  LeaveCategoryId: '',
  LeaveType: 'FullDay',
  HalfDayPart: 'FirstHalf',
  UseSpecialToil: false,
  StartDate: '',
  EndDate: '',
  Reason: '',
};

export interface LeaveRequestFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const LeaveRequestFormDialog = ({ open, onOpenChange }: LeaveRequestFormDialogProps) => {
  const { t } = useTranslation();

  // Only the leave types of the employee's current HR term, default leave first.
  const { options: leaveTypes, isLoading: isLeaveTypesLoading } = useGetMyLeaveTypes();
  const hasNoLeaveTypes = !isLeaveTypesLoading && leaveTypes.length === 0;

  const { mutateAsync: submitLeaveRequest, isPending } = useSubmitLeaveRequest();
  const holdsSplitToilRule = useHasPermission(SPLIT_TOIL_PERMISSION);
  // A sick leave's doctor's document (uploaded on submit), or the doctor sends it.
  const [documents, setDocuments] = useState<File[]>([]);
  const [doctorSends, setDoctorSends] = useState(false);
  const [documentError, setDocumentError] = useState('');

  const form = useForm<LeaveRequestFormValues>({
    resolver: async (data, context, options) => {
      const result = await (
        zodResolver(leaveRequestFormSchema) as unknown as Resolver<LeaveRequestFormValues>
      )(data, context, options);
      if (result.errors) {
        result.errors = Object.fromEntries(
          Object.entries(result.errors).map(([key, error]) => [
            key,
            { ...error, message: t((error as { message?: string }).message ?? '') },
          ])
        );
      }
      return result;
    },
    defaultValues: EMPTY_FORM,
  });

  useEffect(() => {
    if (!open) return;
    form.reset(EMPTY_FORM);
    setDocuments([]);
    setDoctorSends(false);
    setDocumentError('');
  }, [open, form]);

  const leaveCategoryId = form.watch('LeaveCategoryId');
  const leaveType = form.watch('LeaveType');
  const halfDayPart = form.watch('HalfDayPart');
  const startDate = form.watch('StartDate');
  const endDate = form.watch('EndDate');

  useEffect(() => {
    if (leaveType === 'HalfDay' && startDate) {
      form.setValue('EndDate', startDate);
    }
  }, [leaveType, startDate, form]);

  // A half day names its half; a full day sends none (the workflow's rule).
  const isFirstHalfOf = (type: string, part: string) =>
    type === 'HalfDay' ? part === 'FirstHalf' : undefined;

  // The LEAVE_REQUEST_SUBMIT workflow's own count (working days only, and their hours) and what is left of
  // the leave type, so this preview never disagrees with what the request is checked against.
  const {
    data: preview,
    isFetching: isPreviewFetching,
    isLoading: isPreviewLoading,
  } = useLeaveRequestPreview(
    open && leaveCategoryId && startDate && endDate && startDate <= endDate
      ? {
          leaveCategoryId,
          startDate,
          endDate,
          isFirstHalf: isFirstHalfOf(leaveType, halfDayPart),
        }
      : null
  );
  // Special TOIL: the default leave of a holder of the split TOIL rule (the workflow checks both), and
  // only while some is left. Weekend TOIL needs no choice: the leave sheet uses it up first on its own.
  const specialToilLeft = preview?.SpecialToilLeft ?? 0;
  const canUseSpecialToil =
    holdsSplitToilRule && Boolean(preview?.CanPickToilPool) && specialToilLeft > 0;
  // Cleared only by a settled preview: a date change refetches it, and the choice must survive that.
  useEffect(() => {
    if (!isPreviewFetching && !canUseSpecialToil) form.setValue('UseSpecialToil', false);
  }, [isPreviewFetching, canUseSpecialToil, form]);
  const useSpecialToil = form.watch('UseSpecialToil');
  // The workflow refuses it too (LEAVE_EXCEEDS_TOIL_POOL); a longer leave has to be split.
  const exceedsSpecialToil =
    canUseSpecialToil && useSpecialToil && (preview?.LeaveDays ?? 0) > specialToilLeft;
  // While the preview reloads, a ticked special TOIL is not yet known to fit (nor even to be offered).
  const isSpecialToilPending = useSpecialToil && isPreviewFetching;
  const isSickLeave = Boolean(preview?.IsSickLeave);
  const isDocumentRequired = isSickLeave && Boolean(preview?.IsDocumentRequired);
  const canDoctorSend = isSickLeave && Boolean(preview?.CanDoctorSendDocument);
  useEffect(() => {
    if (!canDoctorSend) setDoctorSends(false);
  }, [canDoctorSend]);
  useEffect(() => {
    if (!isDocumentRequired || documents.length || doctorSends) setDocumentError('');
  }, [isDocumentRequired, documents.length, doctorSends]);

  /**
   * A refusal (notice period, max consecutive days, an HR-period split, the allotment or a TOIL pool
   * exceeded, ...) comes back as `IsSuccessful: false` with its own message, toasted by
   * `useSubmitLeaveRequest`; the dialog stays open then.
   */
  const handleSubmit = async (values: LeaveRequestFormValues) => {
    if (exceedsSpecialToil || isSpecialToilPending) return;
    // The workflow refuses it too; this only saves the round trip and the uploads.
    if (isDocumentRequired && !documents.length && !doctorSends) {
      setDocumentError(
        canDoctorSend ? 'SICK_LEAVE_DOCUMENT_OR_DOCTOR_REQUIRED' : 'SICK_LEAVE_DOCUMENT_REQUIRED'
      );
      return;
    }
    try {
      const result = await submitLeaveRequest({
        leaveCategoryId: values.LeaveCategoryId,
        startDate: values.StartDate,
        endDate: values.EndDate,
        isFirstHalf: isFirstHalfOf(values.LeaveType, values.HalfDayPart),
        reason: values.Reason,
        ...(canUseSpecialToil && values.UseSpecialToil
          ? { toilCategory: 'SpecialToil' as const }
          : {}),
        ...(isSickLeave && !doctorSends ? { files: documents } : {}),
        ...(canDoctorSend && doctorSends ? { isDocumentSendByDr: true } : {}),
      });
      if (result.IsSuccessful) onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" alignCloseWithTitle>
        <DialogHeader>
          <DialogTitle>{t('REQUEST_LEAVE')}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="LeaveCategoryId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('LEAVE_CATEGORY')}</FormLabel>
                  {isLeaveTypesLoading ? (
                    <Skeleton className="h-8 w-full" />
                  ) : (
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={hasNoLeaveTypes}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('LEAVE_CATEGORY')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {leaveTypes.map((leaveType) => (
                          <SelectItem key={leaveType.ItemId} value={leaveType.ItemId}>
                            {leaveType.Title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {hasNoLeaveTypes && (
                    <p className="text-sm text-muted-foreground">{t('NO_LEAVE_TYPES_AVAILABLE')}</p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="LeaveType"
              render={({ field }) => (
                // The options beside the title, as in the attendance correction form.
                <FormItem className="flex flex-wrap items-center gap-x-4 gap-y-2 space-y-0">
                  <FormLabel className="whitespace-nowrap">{t('TYPE')}:</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="flex flex-wrap gap-x-4 gap-y-2"
                    >
                      <label className="flex items-center gap-2 text-sm">
                        <RadioGroupItem value="FullDay" />
                        {t('FULL_DAY')}
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <RadioGroupItem value="HalfDay" />
                        {t('HALF_DAY')}
                      </label>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage className="basis-full" />
                </FormItem>
              )}
            />

            {leaveType === 'HalfDay' && (
              <FormField
                control={form.control}
                name="HalfDayPart"
                render={({ field }) => (
                  <FormItem className="flex flex-wrap items-center gap-x-4 gap-y-2 space-y-0">
                    <FormLabel className="whitespace-nowrap">{t('HALF_DAY_PART')}:</FormLabel>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        className="flex flex-wrap gap-x-4 gap-y-2"
                      >
                        <label className="flex items-center gap-2 text-sm">
                          <RadioGroupItem value="FirstHalf" />
                          {t('FIRST_HALF')}
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <RadioGroupItem value="SecondHalf" />
                          {t('SECOND_HALF')}
                        </label>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage className="basis-full" />
                  </FormItem>
                )}
              />
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="StartDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('START_DATE')}</FormLabel>
                    <FormControl>
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="EndDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('END_DATE')}</FormLabel>
                    <FormControl>
                      <DatePicker
                        disabled={leaveType === 'HalfDay'}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* The first count for these dates is on its way: hold its line. */}
            {!preview && isPreviewLoading && <Skeleton className="h-4 w-48" />}

            {preview && (
              <div className="space-y-1 text-sm text-muted-foreground">
                <p>
                  {t('LEAVE_DAY_COUNT_PREVIEW', { count: preview.LeaveDays })}
                  {/* Overtime leave is paid in hours. */}
                  {preview.IsOvertimeLeave && (
                    <> {t('LEAVE_HOURS_PREVIEW', { hours: hoursText(preview.LeaveHours) })}</>
                  )}
                </p>
                {preview.IsOvertimeLeave && preview.RemainingOvertimeHours !== null && (
                  <p
                    className={
                      preview.LeaveHours > preview.RemainingOvertimeHours
                        ? 'text-destructive'
                        : undefined
                    }
                  >
                    {t('OVERTIME_REMAINING_PREVIEW', {
                      hours: hoursText(preview.RemainingOvertimeHours),
                    })}
                  </p>
                )}
              </div>
            )}

            {canUseSpecialToil && (
              <FormField
                control={form.control}
                name="UseSpecialToil"
                render={({ field }) => (
                  <FormItem>
                    <label className="flex items-center gap-2 text-sm">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(value) => field.onChange(value === true)}
                        />
                      </FormControl>
                      {t('USE_SPECIAL_TOIL', { days: specialToilLeft })}
                    </label>
                    {exceedsSpecialToil && (
                      <p className="text-sm text-destructive">
                        {t('LEAVE_EXCEEDS_SPECIAL_TOIL', { days: specialToilLeft })}
                      </p>
                    )}
                  </FormItem>
                )}
              />
            )}

            {isSickLeave && (
              <SickLeaveDocumentField
                files={documents}
                onFilesChange={setDocuments}
                canDoctorSend={canDoctorSend}
                doctorSends={doctorSends}
                onDoctorSendsChange={setDoctorSends}
                requiredDays={isDocumentRequired ? SICK_DOCUMENT_MIN_DAYS : null}
                error={documentError}
                disabled={isPending}
              />
            )}

            <FormField
              control={form.control}
              name="Reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('REASON')}</FormLabel>
                  <FormControl>
                    <Textarea rows={3} maxLength={500} placeholder={t('REASON')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 sm:gap-0">
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button
                type="submit"
                // Spins (and can't be pressed) until the day count for the picked dates is back:
                // the request is checked against that same count.
                loading={isPending || isPreviewFetching}
                disabled={
                  isPending ||
                  isPreviewFetching ||
                  hasNoLeaveTypes ||
                  exceedsSpecialToil ||
                  isSpecialToilPending
                }
              >
                {t('SUBMIT')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
